import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  Calendar,
  Flame,
  Shield,
  TrendingUp,
  Target,
  Zap,
  Award,
  Sparkles,
  PhoneCall,
  AlertTriangle,
  Siren,
  XCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_vee-trading-hub/artifacts/k4o9jcwu_VEE%20CONSULTANCY%20LOGO.png";

// ============ DATA ============
const PAIN_POINTS = [
  { icon: AlertTriangle, title: "Where to Start?", desc: "Stock market terms, charts, news — सब confusing लगता है। Beginners को structured guidance नहीं मिलती।" },
  { icon: Clock, title: "Information Overload", desc: "हर जगह अलग-अलग tips, opinions, courses — कौन सा reliable है? Foundational concepts पहले समझने ज़रूरी हैं।" },
  { icon: TrendingUp, title: "No Clear Framework", desc: "Random tips, social media advice — पर एक systematic learning approach नहीं। Concepts को structured way में study करना ज़रूरी है।" },
  { icon: XCircle, title: "Risk Awareness Missing", desc: "Capital protection और risk management के basic concepts beginners को कम मिलते हैं — यह foundation चाहिए।" },
];

const BENEFITS = [
  { icon: Target, title: "Structured Entry & Exit Logic", desc: "एक clear framework जो आपको systematic decision-making के लिए study करने को मिलेगा।", color: "#10B981" },
  { icon: Zap, title: "Time-Based Market Study", desc: "Market cycles और timing concepts कैसे study करते हैं — conceptually.", color: "#FBBF24" },
  { icon: Shield, title: "Risk-First Thinking", desc: "Capital protection के concepts — educational framework के through।", color: "#F97316" },
  { icon: TrendingUp, title: "Multi-Concept Approach", desc: "Indian stock markets के different segments को एक structured analytical framework से study करना।", color: "#3B82F6" },
  { icon: Award, title: "Clarity Over Confusion", desc: "हर concept के पीछे एक reason — study करना आसान बनाता है।", color: "#EC4899" },
  { icon: Sparkles, title: "Live Chart Study", desc: "Real charts पर live analysis — theoretical concepts को practically समझना।", color: "#10B981" },
];

const FAQ = [
  { q: "क्या यह webinar बिल्कुल free है?", a: "हाँ। यह live webinar 100% free है। आपको सिर्फ register करना है और time पर join करना है।" },
  { q: "क्या मैं beginner हूँ तो join कर सकता हूँ?", a: "बिल्कुल। Webinar beginners और intermediate दोनों के लिए design की गई है। हम basics से advanced तक cover करेंगे।" },
  { q: "Webinar की duration कितनी है?", a: "Webinar लगभग 75 मिनट की होगी — 50 min content + 25 min live Q&A।" },
  { q: "क्या VEE CONSULTANCY SEBI registered है?", a: "VEE Consultancy SEBI registered investment advisor नहीं है। हम सिर्फ़ educational content provide करते हैं — कोई buy/sell tips, signals या advisory service नहीं देते। Sessions केवल learning purposes के लिए हैं।" },
];

// ============ HELPERS ============
const getDefaultDate = () => {
  const d = new Date();
  const day = d.getDay();
  const diff = (6 - day + 7) % 7 || 7;
  d.setDate(d.getDate() + diff);
  d.setHours(19, 30, 0, 0);
  return d;
};
const formatDate = (d) => d.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Kolkata" });
const formatTime = (d) => d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });

// ============ COUNTDOWN ============
function Countdown({ target, size = "lg" }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  const big = size === "lg";
  const Cell = ({ value, label, color }) => (
    <div className="flex flex-col items-center">
      <div
        className={`${big ? "text-4xl md:text-5xl" : "text-2xl md:text-3xl"} font-mono font-bold tabular-nums`}
        style={{ color, textShadow: `0 0 20px ${color}66` }}
      >
        {String(value).padStart(2, "0")}
      </div>
      <div className="text-[9px] md:text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-1 font-mono">
        {label}
      </div>
    </div>
  );
  return (
    <div className="flex items-center justify-center gap-3 md:gap-5" data-testid="countdown-timer">
      <Cell value={days} label="Days" color="#10B981" />
      <div className="text-slate-700 text-2xl">:</div>
      <Cell value={hours} label="Hours" color="#FBBF24" />
      <div className="text-slate-700 text-2xl">:</div>
      <Cell value={mins} label="Min" color="#F97316" />
      <div className="text-slate-700 text-2xl">:</div>
      <Cell value={secs} label="Sec" color="#EF4444" />
    </div>
  );
}

// ============ LIVE TICKER ============
function Ticker() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/ticker`);
        if (!cancelled && r.data?.items?.length) setItems(r.data.items);
      } catch {}
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  if (!items.length) return null;
  const track = [...items, ...items];
  return (
    <div className="relative overflow-hidden border-y border-slate-800 bg-[#0B1220]">
      <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-[#0B1220] to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-[#0B1220] to-transparent z-10 pointer-events-none" />
      <div className="flex marquee-track whitespace-nowrap py-3.5">
        {track.map((it, i) => (
          <div key={i} className="flex items-center gap-3 px-8 font-mono text-xs">
            <span className="text-slate-400 font-semibold">{it.symbol}</span>
            <span className="text-white font-bold">{it.price}</span>
            <span className={it.change_pct >= 0 ? "text-emerald-400 font-bold" : "text-rose-400 font-bold"}>
              {it.change_pct >= 0 ? "▲" : "▼"} {Math.abs(it.change_pct).toFixed(2)}%
            </span>
            <span className="text-slate-700 ml-3">◆</span>
          </div>
        ))}
      </div>
      <div className="absolute top-2 right-4 flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-widest text-emerald-400">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 glow-pulse" />
        Live Market
      </div>
    </div>
  );
}

// ============ URGENCY TOP BAR ============
function UrgencyBar({ target }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target - now);
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return (
    <div className="bg-gradient-to-r from-red-600 via-orange-500 to-red-600 text-white text-center py-2 px-4 text-xs md:text-sm font-semibold relative overflow-hidden">
      <div className="flex items-center justify-center gap-2 flex-wrap">
        <Siren className="w-4 h-4 animate-pulse" />
        <span>FREE LIVE MASTERCLASS STARTS IN:</span>
        <span className="font-mono font-bold tracking-wider">
          {String(h).padStart(2, "0")}h : {String(m).padStart(2, "0")}m : {String(s).padStart(2, "0")}s
        </span>
      </div>
    </div>
  );
}

// ============ MAIN PAGE ============
export default function LandingPage() {
  const navigate = useNavigate();
  const [webinarDate, setWebinarDate] = useState(getDefaultDate());
  const [duration, setDuration] = useState(75);
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", experience: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/config`)
      .then((r) => {
        if (r.data?.webinar_at) {
          const d = new Date(r.data.webinar_at);
          if (!isNaN(d.getTime())) setWebinarDate(d);
        }
        if (r.data?.duration_min) setDuration(r.data.duration_min);
      })
      .catch(() => {});
  }, []);

  const scrollToForm = () => {
    document.getElementById("register")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.phone) {
      toast.error("कृपया Name aur Mobile Number भरें।");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.email || !payload.email.trim()) {
        delete payload.email;
      }
      await axios.post(`${API}/register`, payload);
      setSuccess(true);
      toast.success("Aapki seat confirm ho gayi! 🎉");
      // Meta Pixel — fire Lead conversion event
      if (typeof window !== "undefined" && window.fbq) {
        window.fbq("track", "Lead", {
          content_name: "VEE Masterclass Registration",
          content_category: "webinar_signup",
        });
      }
      // Redirect to Thank You page (Fortune-style 3-step UX)
      const userName = encodeURIComponent(form.name || "Trader");
      setTimeout(() => navigate(`/thank-you?name=${userName}`), 600);
    } catch (err) {
      // Pydantic validation errors return an array of error objects;
      // extract first error's msg for user-friendly display
      const detail = err?.response?.data?.detail;
      let msg = "Registration failed. Kripya phir try karein.";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        msg = detail[0].msg.replace(/^Value error,\s*/i, "");
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#03060D] text-slate-100 font-body relative overflow-hidden">
      {/* ========== URGENCY BAR ========== */}
      <UrgencyBar target={webinarDate.getTime()} />

      {/* ========== NAV ========== */}
      <nav className="sticky top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#03060D]/80 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <a href="#" className="flex items-center gap-3" data-testid="nav-brand">
            <img src={LOGO_URL} alt="VEE Consultancy" className="h-10 w-auto object-contain" />
          </a>
          <div className="hidden md:flex items-center gap-7 text-sm text-slate-300">
            <a href="#learn" className="hover:text-[#10B981] transition-colors">What You Learn</a>
            <a href="#about" className="hover:text-[#10B981] transition-colors">About</a>
            <a href="#faq" className="hover:text-[#10B981] transition-colors">FAQ</a>
          </div>
          <Button
            data-testid="nav-register-btn"
            onClick={scrollToForm}
            className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 rounded-full px-5 h-9 text-xs tracking-wide uppercase font-bold shadow-[0_0_20px_rgba(16,185,129,0.5)]"
          >
            Register Free
          </Button>
        </div>
      </nav>

      {/* ========== HERO ========== */}
      <section className="relative pt-16 pb-20 md:pt-24 md:pb-28 px-4 md:px-8 overflow-hidden">
        <div className="absolute inset-0 mesh-bg" />
        <div className="absolute inset-0 candle-bg opacity-60" />

        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            {/* Multi-color badges */}
            <div className="flex flex-wrap gap-2 mb-6">
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/40 urgency-pulse">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-red-400 font-bold">Live Masterclass</span>
              </div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/40">
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-emerald-400 font-bold">100% Free</span>
              </div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/40">
                <Flame className="w-3 h-3 text-amber-400" />
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-amber-400 font-bold">Educational Session</span>
              </div>
            </div>

            {/* Heading */}
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight text-white mb-6">
              Stock Market{" "}
              <span className="text-emerald-400 italic" style={{ textShadow: "0 0 40px rgba(16,185,129,0.5)" }}>समझना</span>{" "}
              <br className="hidden md:block" />
              अब आसान —{" "}
              <span className="text-gold-shimmer italic">Educational Workshop.</span>
            </h1>

            <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-2xl mb-8">
              Indian stock markets कैसे काम करते हैं, ये एक beginner-friendly educational session है जिसमें आप
              <span className="text-emerald-400 font-semibold"> VEE Consultancy का structured learning framework </span>
              study करेंगे — Indian markets को एक systematic framework में समझने के लिए। (Educational purposes only.)
            </p>

            {/* CTA + Info */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
              <Button
                data-testid="hero-reserve-btn"
                onClick={scrollToForm}
                className="group bg-gradient-to-r from-emerald-500 via-emerald-400 to-emerald-500 text-black hover:scale-105 transition-all duration-300 rounded-full h-14 px-8 text-base font-black tracking-wide neon-green"
              >
                Register for Free Workshop
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span>Educational · 100% Free · Live on Zoom</span>
              </div>
            </div>

            {/* Honest info row — no fake counters */}
            <div className="max-w-xl flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-emerald-400 shrink-0" />
                <span className="text-slate-300">{formatDate(webinarDate)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-400 shrink-0" />
                <span className="text-slate-300">{formatTime(webinarDate)} IST • {duration} min</span>
              </div>
            </div>
          </div>

          {/* Right — Countdown card */}
          <div className="lg:col-span-5 fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative rounded-2xl p-[2px] animated-border">
              <div className="relative rounded-2xl bg-[#0B1220] p-7 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/40">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 glow-pulse" style={{ color: "#EF4444" }} />
                    <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-red-400 font-bold">Starts In</span>
                  </div>
                  <Flame className="w-5 h-5 text-amber-400 glow-pulse" style={{ color: "#FBBF24" }} />
                </div>

                <Countdown target={webinarDate.getTime()} size="lg" />

                <div className="mt-7 pt-6 border-t border-slate-800 space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <Calendar className="w-4 h-4 text-emerald-400 shrink-0" />
                    <span className="text-slate-300">{formatDate(webinarDate)}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Clock className="w-4 h-4 text-amber-400 shrink-0" />
                    <span className="text-slate-300">{formatTime(webinarDate)} IST • {duration} Min Live</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Sparkles className="w-4 h-4 text-orange-400 shrink-0" />
                    <span className="text-slate-300">Live on Zoom</span>
                  </div>
                </div>

                <Button
                  onClick={scrollToForm}
                  className="w-full mt-6 h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 rounded-xl font-bold tracking-wide shadow-[0_0_25px_rgba(16,185,129,0.4)]"
                >
                  Claim Your Seat →
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== PAIN POINTS ========== */}
      <section id="problem" className="relative py-20 md:py-28 px-4 md:px-8 bg-[#050914]">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/30">
              <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-red-400 font-bold">The Problem</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05] mb-5">
              Beginners के सामने अक्सर{" "}
              <span className="italic text-red-400">यही challenges आते हैं</span>
            </h2>
            <p className="text-lg text-slate-400">
              Stock market education में ये common patterns हैं। देखिए क्या आपको relate होता है।
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PAIN_POINTS.map((p, i) => (
              <div
                key={i}
                className="group relative rounded-2xl border border-slate-800 bg-gradient-to-b from-slate-900/60 to-slate-900/20 p-6 hover:border-red-500/40 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center mb-4 shadow-[0_6px_20px_rgba(239,68,68,0.35)]">
                  <p.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-display text-xl text-white mb-2">{p.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== BENEFITS ========== */}
      <section id="learn" className="relative py-20 md:py-28 px-4 md:px-8 overflow-hidden">
        <div className="absolute inset-0 mesh-bg opacity-60" />
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/40">
              <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-emerald-400 font-bold">The VEE System</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05] mb-5">
              जो आप webinar में सीखेंगे —{" "}
              <span className="text-gold-shimmer italic">live market पर।</span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              कोई fluff नहीं, कोई hype नहीं। सिर्फ़ एक <span className="text-emerald-400 font-semibold">proven framework</span> — 6 pillars में।
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b, i) => (
              <div
                key={i}
                className="group relative rounded-2xl border border-slate-800 bg-gradient-to-b from-slate-900/80 to-slate-900/30 p-7 hover:-translate-y-1 transition-all duration-300"
                style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform"
                  style={{ background: `linear-gradient(135deg, ${b.color}, ${b.color}99)`, boxShadow: `0 6px 20px ${b.color}40` }}
                >
                  <b.icon className="w-5 h-5 text-white" />
                </div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2">
                  0{i + 1} / 06
                </div>
                <h3 className="font-display text-xl text-white mb-3">{b.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== MID-PAGE CTA ========== */}
      <section className="relative py-12 md:py-16 px-4 md:px-8 bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-emerald-500/10 border-y border-emerald-500/20 overflow-hidden">
        <div className="absolute inset-0 candle-bg opacity-30" />
        <div className="relative max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 mb-2 px-2.5 py-1 rounded-full bg-red-500/15 border border-red-500/40">
              <Flame className="w-3 h-3 text-red-400" />
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-red-400 font-bold">Limited Seats</span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-white leading-tight">
              Ready to <span className="text-emerald-400 italic">understand</span> markets better?
            </h3>
            <p className="text-sm text-slate-400 mt-1">Register now — it takes just 30 seconds.</p>
          </div>
          <Button
            data-testid="mid-cta-btn"
            onClick={scrollToForm}
            className="bg-gradient-to-r from-emerald-500 via-emerald-400 to-emerald-500 text-black hover:scale-105 transition-all duration-300 rounded-full h-14 px-8 text-base font-black tracking-wide neon-green shrink-0"
          >
            Register for Free Workshop
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* ========== ABOUT ========== */}
      <section id="about" className="relative py-20 md:py-28 px-4 md:px-8">
        <div className="absolute inset-0 mesh-bg opacity-40" />
        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/40">
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-blue-400 font-bold">About VEE Consultancy</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05] mb-6">
            Education-First Approach to the Markets —{" "}
            <span className="italic text-slate-400">Simplified for You.</span>
          </h2>
          <div className="text-lg text-slate-300 leading-relaxed max-w-3xl mx-auto mb-12 space-y-5">
            <p>
              VEE Consultancy believes in teaching <span className="text-emerald-400 font-semibold">concepts, not calls</span>. Instead of tips and signals, we focus on <span className="text-white">frameworks</span> that help you study market structure, time-based logic, and decision-making on your own.
            </p>
            <p>Our approach is simple — <span className="text-white font-semibold">"Rules पहले, emotions बाद में."</span></p>
            <p>In this Masterclass we'll walk you through the same analytical thinking smart traders use daily — so you can understand the <em>why</em> behind every move, not just the <em>what</em>.</p>
          </div>

          <div className="grid grid-cols-3 gap-4 md:gap-6 pt-10 border-t border-slate-800 max-w-3xl mx-auto">
            {[
              { v: "Education", l: "First Philosophy", c: "#10B981" },
              { v: "Structured", l: "Thinking", c: "#FBBF24" },
              { v: "Rules First", l: "No Noise", c: "#F97316" },
            ].map((s, i) => (
              <div key={i}>
                <div
                  className="font-display text-2xl md:text-4xl font-bold"
                  style={{ color: s.c, textShadow: `0 0 30px ${s.c}40` }}
                >
                  {s.v}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-2">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== REGISTRATION ========== */}
      <section id="register" className="relative py-20 md:py-28 px-4 md:px-8 overflow-hidden">
        <div className="absolute inset-0 mesh-bg" />
        <div className="relative max-w-5xl mx-auto">
          <div className="relative rounded-3xl p-[2px] animated-border">
            <div className="relative rounded-3xl bg-[#030711] p-8 md:p-14">
              <div className="grid lg:grid-cols-2 gap-12 items-start">
                {/* Left */}
                <div>
                  <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/40 urgency-pulse">
                    <Flame className="w-3 h-3 text-red-400" />
                    <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-red-400 font-bold">
                      Educational Workshop · Limited Seats
                    </span>
                  </div>
                  <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-white leading-[1.05] mb-5">
                    अपनी{" "}
                    <span className="text-gold-shimmer italic">free seat</span>{" "}
                    अभी reserve करें।
                  </h2>
                  <p className="text-slate-400 leading-relaxed mb-8">
                    Registration 30 seconds में complete होगा। Confirmation WhatsApp और email पर instant मिलेगा।
                  </p>

                  <ul className="space-y-3 mb-8">
                    {[
                      { t: "100% Free Live Webinar", c: "text-emerald-400" },
                      { t: "Live Chart Analysis + Q&A", c: "text-amber-400" },
                      { t: "Live Concept Study + Q&A Session", c: "text-orange-400" },
                      { t: "No Spam. Ever.", c: "text-emerald-400" },
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-slate-200">
                        <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${item.c}`} />
                        {item.t}
                      </li>
                    ))}
                  </ul>

                  {/* Mini countdown */}
                  <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800">
                    <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500 mb-3 text-center">
                      Webinar Starts In
                    </div>
                    <Countdown target={webinarDate.getTime()} size="sm" />
                  </div>
                </div>

                {/* Right — Form */}
                <div>
                  {success ? (
                    <div data-testid="registration-success" className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-8 text-center">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mx-auto mb-5 shadow-[0_0_40px_rgba(16,185,129,0.5)]">
                        <CheckCircle2 className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="font-display text-2xl text-white mb-3">
                        आपकी seat confirm हो गई है! 🎉
                      </h3>
                      <p className="text-slate-300 text-sm mb-4">
                        Redirecting to your next steps…
                      </p>
                      <div className="font-mono text-xs text-slate-500 pt-5 border-t border-emerald-500/20">
                        {formatDate(webinarDate)} • {formatTime(webinarDate)} IST
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4" data-testid="registration-form">
                      <div>
                        <Label htmlFor="name" className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-2 block">
                          Full Name *
                        </Label>
                        <Input
                          id="name"
                          data-testid="registration-name-input"
                          type="text"
                          placeholder="आपका पूरा नाम"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          required
                          className="h-12 bg-slate-900/80 border-slate-700 focus:border-emerald-500 text-white placeholder:text-slate-600 rounded-lg"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-2 block">
                          Email <span className="text-slate-500 normal-case tracking-normal">(Optional)</span>
                        </Label>
                        <Input
                          id="email"
                          data-testid="registration-email-input"
                          type="email"
                          placeholder="you@example.com (optional)"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          className="h-12 bg-slate-900/80 border-slate-700 focus:border-emerald-500 text-white placeholder:text-slate-600 rounded-lg"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-2 block">
                          WhatsApp Number *
                        </Label>
                        <Input
                          id="phone"
                          data-testid="registration-phone-input"
                          type="tel"
                          inputMode="numeric"
                          maxLength={15}
                          placeholder="10-digit mobile (e.g. 9876543210)"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          required
                          className="h-12 bg-slate-900/80 border-slate-700 focus:border-emerald-500 text-white placeholder:text-slate-600 rounded-lg"
                        />
                      </div>
                      <div>
                        <Label htmlFor="city" className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-2 block">
                          City <span className="text-slate-600 normal-case tracking-normal">(optional)</span>
                        </Label>
                        <Input
                          id="city"
                          data-testid="registration-city-input"
                          type="text"
                          maxLength={80}
                          placeholder="e.g. Mumbai, Delhi, Bangalore"
                          value={form.city}
                          onChange={(e) => setForm({ ...form, city: e.target.value })}
                          className="h-12 bg-slate-900/80 border-slate-700 focus:border-emerald-500 text-white placeholder:text-slate-600 rounded-lg"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-2 block">
                          Learning Background
                        </Label>
                        <div className="grid grid-cols-3 gap-2">
                          {["Beginner", "Intermediate", "Advanced"].map((opt) => (
                            <button
                              key={opt}
                              type="button"
                              data-testid={`exp-${opt.toLowerCase()}-btn`}
                              onClick={() => setForm({ ...form, experience: opt })}
                              className={`h-10 rounded-lg text-xs font-bold transition-all ${
                                form.experience === opt
                                  ? "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-[0_0_20px_rgba(16,185,129,0.4)]"
                                  : "bg-slate-900/80 text-slate-400 border border-slate-700 hover:border-emerald-500/50"
                              }`}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      </div>

                      <Button
                        type="submit"
                        data-testid="registration-submit-button"
                        disabled={submitting}
                        className="w-full h-14 bg-gradient-to-r from-emerald-500 via-emerald-400 to-emerald-500 text-black hover:scale-[1.02] transition-all duration-300 rounded-xl text-base font-black tracking-wide neon-green disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100"
                      >
                        {submitting ? "Registering..." : "Register for Free Workshop"}
                      </Button>

                      <p className="text-[10px] text-slate-500 text-center leading-relaxed">
                        🔒 By registering, you agree to receive webinar & course updates from VEE Consultancy via <strong className="text-slate-400">WhatsApp, Email, and SMS</strong>. You can unsubscribe anytime. We never share your data with third parties.
                      </p>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FAQ ========== */}
      <section id="faq" className="relative py-20 md:py-28 px-4 md:px-8 bg-[#050914]">
        <div className="max-w-3xl mx-auto">
          <div className="mb-12 text-center">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/40">
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-blue-400 font-bold">FAQ</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-white leading-[1.05]">
              हर सवाल का जवाब।
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {FAQ.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border border-slate-800 rounded-2xl px-6 bg-slate-900/50 hover:border-emerald-500/30 transition-colors"
              >
                <AccordionTrigger
                  data-testid={`faq-item-${i}`}
                  className="text-left font-display text-lg text-white hover:text-emerald-400 hover:no-underline py-5"
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="relative py-20 md:py-28 px-4 md:px-8 overflow-hidden border-t border-slate-800">
        <div className="absolute inset-0 mesh-bg" />
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/40 urgency-pulse">
            <Siren className="w-4 h-4 text-red-400" />
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-red-400 font-bold">Limited Capacity</span>
          </div>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.02] mb-6">
            Stock Market की{" "}
            <span className="text-gold-shimmer italic">basics समझिए।</span>
          </h2>
          <p className="text-lg text-slate-400 leading-relaxed mb-8 max-w-2xl mx-auto">
            Free educational workshop, zero obligation. एक structured framework जो आपको markets को systematically समझने में मदद करेगा। (Educational purposes only.)
          </p>

          <div className="max-w-md mx-auto mb-10 p-5 rounded-2xl bg-slate-900/60 border border-slate-800">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500 mb-3">
              Webinar Starts In
            </div>
            <Countdown target={webinarDate.getTime()} size="lg" />
          </div>

          <Button
            data-testid="final-cta-btn"
            onClick={scrollToForm}
            className="bg-gradient-to-r from-emerald-500 via-emerald-400 to-emerald-500 text-black hover:scale-105 transition-all duration-300 rounded-full h-16 px-12 text-lg font-black tracking-wide neon-green"
          >
            Register for Free Workshop
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <div className="mt-4 text-xs text-slate-500">
            Registrations close when the workshop begins.
          </div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="border-t border-slate-800 py-14 px-4 md:px-8 bg-[#030711]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-10">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="VEE Consultancy" className="h-12 w-auto object-contain" />
            </div>
            <div className="flex flex-wrap items-center gap-4 md:gap-6 text-xs text-slate-500 font-mono uppercase tracking-[0.2em]">
              <a href="/terms" className="hover:text-emerald-400 transition-colors">Terms & Conditions</a>
              <a href="/privacy" className="hover:text-emerald-400 transition-colors">Privacy Policy</a>
              <a href="/what-we-provide" className="hover:text-emerald-400 transition-colors">What We Provide</a>
              <a href="/contact" className="hover:text-emerald-400 transition-colors">Contact</a>
            </div>
          </div>

          <div className="pt-8 border-t border-slate-800 space-y-4 text-[11px] leading-relaxed text-slate-500 max-w-5xl">
            <p>
              <strong className="text-slate-300 uppercase tracking-wider text-[10px]">Disclaimer:</strong>{" "}
              This Masterclass is designed purely for <strong className="text-slate-400">educational and informational purposes</strong>. The content shared focuses on market structure, time-based analysis concepts, and analytical frameworks to help participants study and understand market behaviour more systematically.
            </p>
            <p>
              No part of the session should be considered <strong className="text-slate-400">investment advice, trading recommendations, or a solicitation</strong> to buy or sell any financial instrument. Examples, illustrations, and historical references (if any) are used only to explain concepts and do not represent future outcomes.
            </p>
            <p>
              Financial markets involve risk, and individual decisions and outcomes can vary significantly based on experience, discipline, and market conditions. Participants are encouraged to apply independent judgment and, where appropriate, consult qualified financial professionals before making any financial decisions.
            </p>
            <p>
              By attending this Masterclass, you <strong className="text-slate-400">acknowledge</strong> that the learning is conceptual in nature, and responsibility for applying any ideas or frameworks rests solely with the participant.
            </p>
            <p className="pt-2">
              This website is <strong className="text-slate-400">not affiliated with, endorsed by, or sponsored by Meta (Facebook / Instagram) or Google</strong>. The names "Facebook", "Instagram", and "Google" are trademarks of their respective owners.
            </p>
            <p className="pt-4 text-slate-600">
              © {new Date().getFullYear()} VEE Consultancy. All Rights Reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp CTA */}
      <a
        data-testid="whatsapp-cta"
        href="https://wa.me/919662518986?text=JOIN"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-40 group flex items-center gap-3 pl-4 pr-5 h-12 rounded-full bg-gradient-to-r from-[#25D366] to-[#1fb855] text-white transition-all duration-300 shadow-[0_8px_30px_rgba(37,211,102,0.5)] hover:-translate-y-0.5 hover:scale-105"
      >
        <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
          <PhoneCall className="w-3.5 h-3.5" />
        </span>
        <span className="text-sm font-bold">WhatsApp "JOIN"</span>
      </a>
    </div>
  );
}
